/* 그림·툴패스(engrave) 예제 업로드 검증 — 순수 함수(테스트 가능).

   왜 필요한가: 툴패스 예제와 코드 예제는 **확장자가 둘 다 `.huenit`** 인데 내용이 다르다.
     · 코드 프로젝트   .huenit = gzip + tar
     · 엔그레이브 프로젝트 .huenit = 평문 JSON
   사람이 파일명만 보고는 구분할 수 없으므로 매직바이트로 판별해 잘못된 섹션에 발행되는
   사고를 업로드 시점에 막는다(앱 engrave_file_actions.dart 의 라우팅 기준과 동일).
   그림도 확장자만 믿지 않고 시그니처를 확인한다(예: .txt 를 .png 로 바꿔 올린 경우). */
import { SOURCE_EXTS, normalizeSourceExt } from '../../data/mappers.js';

export const TOOLPATH_EXT = 'huenit';
export const PICTURE_ACCEPT = SOURCE_EXTS.map((e) => '.' + e);
export const TOOLPATH_ACCEPT = ['.' + TOOLPATH_EXT];

export const acceptFor = (kind) => (kind === 'toolpath' ? TOOLPATH_ACCEPT : PICTURE_ACCEPT);

/* 앱 Image.network 가 바로 렌더하는 래스터 포맷 — 썸네일 자동 채움 대상. */
const RASTER = new Set(['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp']);
export const isRasterExt = (ext) => RASTER.has(String(ext || '').toLowerCase());

/* 업로드 MIME — S3 ContentType. 목록 밖이면 옥텟 스트림. */
const MIME = {
  svg: 'image/svg+xml', dxf: 'image/vnd.dxf', png: 'image/png', jpg: 'image/jpeg',
  jpeg: 'image/jpeg', bmp: 'image/bmp', gif: 'image/gif', webp: 'image/webp',
};
export const contentTypeFor = (kind, ext) => (kind === 'toolpath'
  ? 'application/json; charset=utf-8'
  : (MIME[String(ext || '').toLowerCase()] || 'application/octet-stream'));

/* 파일명 검증 → { ok, ext, error }. */
export function validateFileName(kind, name) {
  const n = String(name || '');
  if (!n) return { ok: false, ext: '', error: '파일을 선택하세요.' };
  if (kind === 'toolpath') {
    return n.toLowerCase().endsWith('.' + TOOLPATH_EXT)
      ? { ok: true, ext: TOOLPATH_EXT, error: '' }
      : { ok: false, ext: '', error: '툴패스 예제는 .huenit 파일만 올릴 수 있습니다.' };
  }
  const ext = normalizeSourceExt(n);
  return ext
    ? { ok: true, ext, error: '' }
    : { ok: false, ext: '', error: `그림 예제는 ${SOURCE_EXTS.join(', ')} 파일만 올릴 수 있습니다.` };
}

const startsWith = (bytes, sig) => sig.every((b, i) => bytes[i] === b);
const asText = (bytes, n) => {
  try { return new TextDecoder('utf-8').decode(bytes.slice(0, n)).replace(/^﻿/, ''); }
  catch (e) { return ''; }
};

/* 툴패스 본문 검증 — gzip/zip 이면 코드 프로젝트이므로 거부, 그 외엔 JSON 파싱 확인. */
export function validateToolpathBuffer(buffer) {
  const bytes = new Uint8Array(buffer || new ArrayBuffer(0));
  if (bytes.length === 0) return { ok: false, error: '빈 파일입니다.' };
  if (startsWith(bytes, [0x1f, 0x8b])) {
    return { ok: false, error: '코드 프로젝트(.huenit) 파일입니다 — 툴패스 예제는 드로잉·레이저 탭에서 저장한 파일이어야 합니다.' };
  }
  if (startsWith(bytes, [0x50, 0x4b])) {   // 'PK' — zip/kfpkg
    return { ok: false, error: '압축(zip) 파일입니다 — 툴패스 예제는 드로잉·레이저 탭에서 저장한 파일이어야 합니다.' };
  }
  let parsed;
  try { parsed = JSON.parse(asText(bytes, bytes.length)); }
  catch (e) { return { ok: false, error: 'JSON 형식이 아닙니다 — 엔그레이브 프로젝트 파일인지 확인하세요.' }; }
  if (!parsed || typeof parsed !== 'object') {
    return { ok: false, error: 'JSON 객체가 아닙니다 — 엔그레이브 프로젝트 파일인지 확인하세요.' };
  }
  return { ok: true, error: '' };
}

/* 그림 본문 검증 — 확장자와 실제 시그니처가 맞는지. svg/dxf 는 텍스트라 내용으로 확인. */
export function validatePictureBuffer(ext, buffer) {
  const bytes = new Uint8Array(buffer || new ArrayBuffer(0));
  if (bytes.length === 0) return { ok: false, error: '빈 파일입니다.' };
  const e = String(ext || '').toLowerCase();
  const mismatch = { ok: false, error: `파일 내용이 .${e} 형식이 아닙니다 — 확장자만 바꾼 파일인지 확인하세요.` };
  if (e === 'png') return startsWith(bytes, [0x89, 0x50, 0x4e, 0x47]) ? { ok: true, error: '' } : mismatch;
  if (e === 'jpg' || e === 'jpeg') return startsWith(bytes, [0xff, 0xd8, 0xff]) ? { ok: true, error: '' } : mismatch;
  if (e === 'gif') return startsWith(bytes, [0x47, 0x49, 0x46]) ? { ok: true, error: '' } : mismatch;
  if (e === 'bmp') return startsWith(bytes, [0x42, 0x4d]) ? { ok: true, error: '' } : mismatch;
  if (e === 'webp') {
    const ok = startsWith(bytes, [0x52, 0x49, 0x46, 0x46])
      && [0x57, 0x45, 0x42, 0x50].every((b, i) => bytes[8 + i] === b);
    return ok ? { ok: true, error: '' } : mismatch;
  }
  if (e === 'svg') {
    return /<svg[\s>]/i.test(asText(bytes, 4096)) ? { ok: true, error: '' } : mismatch;
  }
  if (e === 'dxf') {
    // DXF 는 ASCII(섹션 헤더 "SECTION") 또는 바이너리("AutoCAD Binary DXF") 둘 다 있다.
    const head = asText(bytes, 4096);
    return (/SECTION/i.test(head) || /AutoCAD Binary DXF/i.test(head)) ? { ok: true, error: '' } : mismatch;
  }
  return { ok: false, error: `지원하지 않는 확장자입니다: .${e}` };
}

/* 종류에 맞는 본문 검증 진입점. */
export const validateBuffer = (kind, ext, buffer) => (kind === 'toolpath'
  ? validateToolpathBuffer(buffer)
  : validatePictureBuffer(ext, buffer));
